import React, { useState } from 'react';
import { FileText, Image, Folder, Eye } from 'lucide-react';
import { Asset } from './FileExplorer';

interface FileListProps {
  assets: Asset[];
  selectedAssets: Asset[];
  onAssetSelect: (asset: Asset) => void;
  onViewDetails: (asset: Asset) => void;
  allowedFileTypes?: string[];
  isPicker?: boolean;
  multiSelect?: boolean;
  onNavigate: (folder: string) => void;
}

export const FileList: React.FC<FileListProps> = ({
  assets,
  selectedAssets,
  onAssetSelect,
  onViewDetails,
  allowedFileTypes,
  isPicker = false,
  multiSelect = true,
  onNavigate,
}) => {
  const [hoveredFile, setHoveredFile] = useState<string | null>(null);

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return <Image className="w-6 h-6 text-blue-500" />;
    if (mimeType === 'folder') return <Folder className="w-6 h-6 text-yellow-500" />;
    return <FileText className="w-6 h-6 text-gray-500" />;
  };

  const isSelectable = (asset: Asset) => {
    if (asset.mimeType === 'folder') return true; // Folders are always selectable
    if (!allowedFileTypes || allowedFileTypes.length === 0) return true;
    return allowedFileTypes.includes(asset.mimeType);
  };

  const handleRowClick = (asset: Asset, e: React.MouseEvent) => {
    if (asset.mimeType === 'folder') {
      onNavigate(asset.originalFile);
    } else if (isSelectable(asset) && !(e.target as HTMLElement).closest('.view-details-button')) {
      onAssetSelect(asset);
    }
  };

  return (
    <div className="bg-white rounded-xl ">
      {assets.map((asset) => {
        const isSelected = selectedAssets.some((a) => a.id === asset.id);
        const selectable = isSelectable(asset);
        return (
          <div
            key={asset.originalFile}
            className={`flex items-center p-4 hover:bg-gray-50 transition duration-150 border-b border-gray-100 ease-in-out ${
              isSelected ? 'bg-blue-50' : ''
            } ${selectable ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
            onClick={(e) => handleRowClick(asset, e)}
            onMouseEnter={() => setHoveredFile(asset.id || '')}
            onMouseLeave={() => setHoveredFile(null)}
          >
            {multiSelect && (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => selectable && onAssetSelect(asset)}
                className="mr-4 pointer-events-none"
                disabled={!selectable}
              />
            )}
            <div className="flex-shrink-0 mr-4">
              {asset.mimeType.startsWith('image/') ? (
                <img src={asset.thumnailImage} alt={asset.filename} className="w-6 h-6 object-cover rounded" />
              ) : (
                getFileIcon(asset.mimeType)
              )}
            </div>
            <div className="flex-grow">
              <p className="text-sm font-medium text-gray-800">{asset.filename}</p>
              <p className="text-xs text-gray-500">{(asset.size / 1024 / 1024).toFixed(2)} MB</p>
            </div>
            <p className="text-xs text-gray-500 mr-4">
              {new Date(asset.createdAt || '').toLocaleDateString()}
            </p>
            {!isPicker && hoveredFile === asset.id && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onViewDetails(asset);
                }}
                className="view-details-button text-blue-500 hover:text-blue-700 transition"
              >
                <Eye size={20} />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};
